/**
 * Ledger Connect Form
 *
 * Form to connect a Ledger hardware wallet
 */

import { useState } from "react";
import { ArrowLeft, HardDrive, Loader2, AlertCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useBrowserWallet,
  useBrowserWalletStore,
} from "@nullaone/wallet-react";
import { DERIVATION_PATHS } from "@nullaone/wallet-core";

type ConnectStep = "instructions" | "connecting" | "success";

interface LedgerConnectState {
  step: ConnectStep;
  path: string;
  address: string | null;
}

export function LedgerConnectForm() {
  const { t } = useTranslation();
  const { connectLedger, connecting, closeModal } = useBrowserWallet();
  const { setModalStep } = useBrowserWalletStore();

  const [state, setState] = useState<LedgerConnectState>({
    step: "instructions",
    path: DERIVATION_PATHS.DEFAULT,
    address: null,
  });

  const handleConnect = async () => {
    setState((prev) => ({ ...prev, step: "connecting" }));

    try {
      await connectLedger(state.path);
      closeModal();
    } catch {
      setState((prev) => ({ ...prev, step: "instructions" }));
    }
  };

  return (
    <div className="space-y-6 py-4">
      <Button
        variant="ghost"
        size="sm"
        className="gap-2"
        onClick={() => setModalStep("select")}
        disabled={state.step === "connecting" || connecting}
      >
        <ArrowLeft className="h-4 w-4" />
        {t("wallet.ledger.back")}
      </Button>

      {state.step === "instructions" && (
        <div className="space-y-6">
          {/* Instructions */}
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-medium">
                1
              </div>
              <div className="space-y-1">
                <p className="font-medium">{t("wallet.ledger.step1Title")}</p>
                <p className="text-sm text-muted-foreground">
                  {t("wallet.ledger.step1Desc")}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-medium">
                2
              </div>
              <div className="space-y-1">
                <p className="font-medium">{t("wallet.ledger.step2Title")}</p>
                <p className="text-sm text-muted-foreground">
                  {t("wallet.ledger.step2Desc")}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-medium">
                3
              </div>
              <div className="space-y-1">
                <p className="font-medium">{t("wallet.ledger.step3Title")}</p>
                <p className="text-sm text-muted-foreground">
                  {t("wallet.ledger.step3Desc")}
                </p>
              </div>
            </div>
          </div>

          {/* Derivation path selector */}
          <div className="space-y-2">
            <Label>{t("wallet.ledger.pathLabel")}</Label>
            <Select
              value={state.path}
              onValueChange={(value) =>
                setState((prev) => ({ ...prev, path: value }))
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={DERIVATION_PATHS.DEFAULT}>
                  {t("wallet.ledger.pathDefault", {
                    path: DERIVATION_PATHS.DEFAULT,
                  })}
                </SelectItem>
                <SelectItem value={DERIVATION_PATHS.LEDGER_LIVE}>
                  {t("wallet.ledger.pathLive", {
                    path: DERIVATION_PATHS.LEDGER_LIVE,
                  })}
                </SelectItem>
                <SelectItem value={DERIVATION_PATHS.ACCOUNT_1}>
                  {t("wallet.ledger.pathAccount", {
                    n: 2,
                    path: DERIVATION_PATHS.ACCOUNT_1,
                  })}
                </SelectItem>
                <SelectItem value={DERIVATION_PATHS.ACCOUNT_2}>
                  {t("wallet.ledger.pathAccount", {
                    n: 3,
                    path: DERIVATION_PATHS.ACCOUNT_2,
                  })}
                </SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {t("wallet.ledger.pathHint")}
            </p>
          </div>

          <Button
            className="w-full"
            size="lg"
            onClick={handleConnect}
            disabled={connecting}
          >
            <HardDrive className="mr-2 h-4 w-4" />
            {t("wallet.ledger.submit")}
          </Button>
        </div>
      )}

      {state.step === "connecting" && (
        <div className="flex flex-col items-center gap-6 py-8">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
          <div className="text-center space-y-2">
            <p className="font-medium">{t("wallet.ledger.connectingTitle")}</p>
            <p className="text-sm text-muted-foreground">
              {t("wallet.ledger.connectingDesc")}
            </p>
          </div>
        </div>
      )}

      {/* Warning */}
      <div className="rounded-lg border border-warning/40 bg-warning/10 p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-warning-dark mt-0.5" />
          <div className="space-y-1">
            <p className="text-sm font-medium text-warning-dark">
              {t("wallet.ledger.blindWarningTitle")}
            </p>
            <p className="text-xs text-warning-dark/80">
              {t("wallet.ledger.blindWarningDesc")}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
